export interface DagNode {
  id: string;
  debugLabel?: string;
  requiredBunjas?: DagNode[];
}

interface Column {
  node: DagNode | null;
  color: number;
}

enum GraphState {
  GRAPH_PADDING,
  GRAPH_SKIP,
  GRAPH_PRE_NODE,
  GRAPH_NODE,
  GRAPH_POST_MERGE,
  GRAPH_COLLAPSING,
}

class Graph {
  node: DagNode | null = null;
  numParents = 0;
  width = 0;
  expansionRow = 0;
  state: GraphState = GraphState.GRAPH_PADDING;
  prevState: GraphState = GraphState.GRAPH_PADDING;
  nodeIndex = 0;
  prevNodeIndex = 0;
  mergeLayout = 0;
  edgesAdded = 0;
  prevEdgesAdded = 0;
  numColumns = 0;
  numNewColumns = 0;
  mappingSize = 0;
  columns: Column[] = [];
  newColumns: Column[] = [];
  mapping: number[] = [];
  oldMapping: number[] = [];
  defaultColumnColor = 0;

  constructor(public numColors: number = 6) {
    this.defaultColumnColor = numColors - 1;
    const capacity = 30;
    this.columns = Array.from({ length: capacity });
    this.newColumns = Array.from({ length: capacity });
    this.mapping = Array.from({ length: capacity * 2 }, () => -1);
    this.oldMapping = Array.from({ length: capacity * 2 }, () => -1);
  }

  findNewColumnByNode(node: DagNode): number {
    for (let i = 0; i < this.numNewColumns; i++) {
      if (this.newColumns[i]?.node === node) {
        return i;
      }
    }
    return -1;
  }

  incrementColumnColor(): void {
    this.defaultColumnColor = (this.defaultColumnColor + 1) % this.numColors;
  }

  findNodeColor(node: DagNode): number {
    for (let i = 0; i < this.numColumns; i++) {
      if (this.columns[i]?.node === node) {
        return this.columns[i].color;
      }
    }
    return this.defaultColumnColor;
  }

  insertIntoNewColumns(node: DagNode, idx: number): void {
    let i = this.findNewColumnByNode(node);
    let mappingIdx: number;

    if (i < 0) {
      i = this.numNewColumns++;
      this.newColumns[i] = {
        node: node,
        color: this.findNodeColor(node),
      };
    }

    if (this.numParents > 1 && idx > -1 && this.mergeLayout === -1) {
      const dist = idx - i;
      const shift = dist > 1 ? 2 * dist - 3 : 1;

      this.mergeLayout = dist > 0 ? 0 : 1;
      this.edgesAdded = this.numParents + this.mergeLayout - 2;

      mappingIdx = this.width + (this.mergeLayout - 1) * shift;
      this.width += 2 * this.mergeLayout;
    } else if (this.edgesAdded > 0 && i === this.mapping[this.width - 2]) {
      mappingIdx = this.width - 2;
      this.edgesAdded = -1;
    } else {
      mappingIdx = this.width;
      this.width += 2;
    }

    this.mapping[mappingIdx] = i;
  }

  updateColumns(): void {
    // Swap columns with newColumns
    const tmpColumns = this.columns;
    this.columns = this.newColumns;
    this.newColumns = tmpColumns;
    this.numColumns = this.numNewColumns;
    this.numNewColumns = 0;

    const maxNewColumns = this.numColumns + this.numParents;

    // Clear mapping
    this.mappingSize = 2 * maxNewColumns;
    for (let i = 0; i < this.mappingSize; i++) {
      this.mapping[i] = -1;
    }

    this.width = 0;
    this.prevEdgesAdded = this.edgesAdded;
    this.edgesAdded = 0;

    // Populate newColumns and mapping
    let seenThis = false;
    let isNodeInColumns = true;

    for (let i = 0; i <= this.numColumns; i++) {
      let colNode: DagNode | null;

      if (i === this.numColumns) {
        if (seenThis) break;
        isNodeInColumns = false;
        colNode = this.node;
      } else {
        colNode = this.columns[i]?.node || null;
      }

      if (colNode === this.node) {
        seenThis = true;
        this.nodeIndex = i;
        this.mergeLayout = -1;

        const parents = this.node?.requiredBunjas || [];
        for (const parent of parents) {
          if (this.numParents > 1 || !isNodeInColumns) {
            this.incrementColumnColor();
          }
          this.insertIntoNewColumns(parent, i);
        }

        if (this.numParents === 0) {
          this.width += 2;
        }
      } else if (colNode) {
        this.insertIntoNewColumns(colNode, -1);
      }
    }

    // Shrink mappingSize
    while (this.mappingSize > 1 && this.mapping[this.mappingSize - 1] < 0) {
      this.mappingSize--;
    }
  }

  updateState(s: GraphState): void {
    this.prevState = this.state;
    this.state = s;
  }

  update(node: DagNode): void {
    this.node = node;
    this.numParents = node.requiredBunjas?.length || 0;
    this.nodeIndex = 0;
    this.expansionRow = 0;

    this.updateColumns();

    if (this.numParents > 1) {
      this.updateState(GraphState.GRAPH_POST_MERGE);
    } else if (this.isMappingCorrect()) {
      this.updateState(GraphState.GRAPH_PADDING);
    } else {
      this.updateState(GraphState.GRAPH_COLLAPSING);
    }

    this.prevNodeIndex = this.nodeIndex;
  }

  isMappingCorrect(): boolean {
    for (let i = 0; i < this.mappingSize; i++) {
      const target = this.mapping[i];
      if (target < 0) continue;
      if (target === Math.floor(i / 2)) continue;
      return false;
    }
    return true;
  }

  outputCollapsingLine(): { chars: string[]; colors: number[] } {
    const chars: string[] = [];
    const colors: number[] = [];

    // Swap mapping arrays
    const tmp = this.mapping;
    this.mapping = this.oldMapping;
    this.oldMapping = tmp;

    // Clear mapping
    for (let i = 0; i < this.mappingSize; i++) {
      this.mapping[i] = -1;
    }

    let usedHorizontal = false;
    let horizontalEdge = -1;
    let horizontalEdgeTarget = -1;

    // Process oldMapping
    for (let i = 0; i < this.mappingSize; i++) {
      const target = this.oldMapping[i];
      if (target < 0) continue;

      if (target * 2 === i) {
        // Already in correct place
        this.mapping[i] = target;
      } else if (this.mapping[i - 1] < 0) {
        // Move left by one
        this.mapping[i - 1] = target;
        if (horizontalEdge === -1) {
          horizontalEdge = i;
          horizontalEdgeTarget = target;
          for (let j = target * 2 + 3; j < i - 2; j += 2) {
            this.mapping[j] = target;
          }
        }
      } else if (this.mapping[i - 1] === target) {
        // Merge with existing line
        // Do nothing
      } else {
        // Cross over
        this.mapping[i - 2] = target;
        if (horizontalEdge === -1) {
          horizontalEdgeTarget = target;
          horizontalEdge = i - 1;
          for (let j = target * 2 + 3; j < i - 2; j += 2) {
            this.mapping[j] = target;
          }
        }
      }
    }

    // Copy to oldMapping
    for (let i = 0; i < this.mappingSize; i++) {
      this.oldMapping[i] = this.mapping[i];
    }

    // Shrink mappingSize
    if (this.mapping[this.mappingSize - 1] < 0) {
      this.mappingSize--;
    }

    // Output line
    for (let i = 0; i < this.mappingSize; i++) {
      const target = this.mapping[i];
      if (target < 0) {
        chars.push(' ');
        colors.push(0);
      } else if (target * 2 === i) {
        chars.push('|');
        colors.push(this.newColumns[target]?.color || 0);
      } else if (target === horizontalEdgeTarget && i !== horizontalEdge - 1) {
        if (i !== target * 2 + 3) {
          this.mapping[i] = -1;
        }
        usedHorizontal = true;
        chars.push('_');
        colors.push(this.newColumns[target]?.color || 0);
      } else {
        if (usedHorizontal && i < horizontalEdge) {
          this.mapping[i] = -1;
        }
        chars.push('/');
        colors.push(this.newColumns[target]?.color || 0);
      }
    }

    // Update state
    if (this.isMappingCorrect()) {
      this.updateState(GraphState.GRAPH_PADDING);
    }

    return { chars, colors };
  }
}

export interface GraphRow {
  type: 'node' | 'post_merge' | 'collapsing';
  node?: DagNode;
  segments: Array<{ char: string; color: number }>;
}

// Build node list using Kahn's algorithm
function buildNodeList(root: DagNode): DagNode[] {
  const nodes: DagNode[] = [];
  const indegree = new Map<string, number>();
  const queue: DagNode[] = [];
  const allNodes = new Set<DagNode>();

  // Collect all nodes and initialize indegree
  function collectNodes(node: DagNode) {
    if (allNodes.has(node)) return;
    allNodes.add(node);
    indegree.set(node.id, 0);

    const parents = node.requiredBunjas || [];
    for (const parent of parents) {
      collectNodes(parent);
    }
  }

  collectNodes(root);

  // Calculate indegree (number of children)
  Array.from(allNodes).forEach((node) => {
    const parents = node.requiredBunjas || [];
    for (const parent of parents) {
      indegree.set(parent.id, (indegree.get(parent.id) || 0) + 1);
    }
  });

  // Find tips (nodes with indegree 0 = no children)
  const tips: DagNode[] = [];
  Array.from(allNodes).forEach((node) => {
    if (indegree.get(node.id) === 0) {
      tips.push(node);
    }
  });

  // Reverse the tips
  tips.reverse();
  queue.push(...tips);

  // Process queue
  while (queue.length > 0) {
    const node = queue.shift()!;
    nodes.push(node);

    const parents = node.requiredBunjas || [];
    // Process parents in REVERSE order
    for (let i = parents.length - 1; i >= 0; i--) {
      const parent = parents[i];
      const degree = indegree.get(parent.id)!;
      indegree.set(parent.id, degree - 1);

      // When indegree becomes 0, all children processed, can emit parent
      if (degree - 1 === 0) {
        queue.push(parent);
      }
    }
  }

  return nodes;
}

export function buildGraphRows(rootNode: DagNode): GraphRow[] {
  const rows: GraphRow[] = [];
  const graph = new Graph(6);
  const nodes = buildNodeList(rootNode);

  for (const node of nodes) {
    graph.update(node);

    // Output node line
    const nodeSegments: Array<{ char: string; color: number }> = [];
    let seenThis = false;

    for (let i = 0; i <= graph.numColumns; i++) {
      let col: Column | undefined;
      let colNode: DagNode | null;

      if (i === graph.numColumns) {
        if (seenThis) break;
        colNode = graph.node;
        col = { node: colNode, color: graph.defaultColumnColor };
      } else {
        col = graph.columns[i];
        colNode = col?.node || null;
      }

      if (colNode === graph.node) {
        seenThis = true;
        // Output node char
        if (graph.numParents > 2) {
          nodeSegments.push({ char: '*', color: col?.color || 0 });
          nodeSegments.push({ char: '-', color: col?.color || 0 });
          nodeSegments.push({ char: '.', color: col?.color || 0 });
        } else {
          nodeSegments.push({ char: '*', color: col?.color || 0 });
        }
      } else if (seenThis && graph.edgesAdded > 1) {
        nodeSegments.push({ char: '\\', color: col?.color || 0 });
      } else if (seenThis && graph.edgesAdded === 1) {
        if (graph.prevState === GraphState.GRAPH_POST_MERGE && graph.prevEdgesAdded > 0 && graph.prevNodeIndex < i) {
          nodeSegments.push({ char: '\\', color: col?.color || 0 });
        } else {
          nodeSegments.push({ char: '|', color: col?.color || 0 });
        }
      } else if (
        graph.prevState === GraphState.GRAPH_COLLAPSING &&
        graph.oldMapping[2 * i + 1] === i &&
        graph.mapping[2 * i] < i
      ) {
        nodeSegments.push({ char: '/', color: col?.color || 0 });
      } else {
        nodeSegments.push({ char: '|', color: col?.color || 0 });
      }

      nodeSegments.push({ char: ' ', color: 0 });
    }

    rows.push({
      type: 'node',
      node: node,
      segments: nodeSegments,
    });

    // Update state
    if (graph.numParents > 1) {
      graph.updateState(GraphState.GRAPH_POST_MERGE);
    } else if (graph.isMappingCorrect()) {
      graph.updateState(GraphState.GRAPH_PADDING);
    } else {
      graph.updateState(GraphState.GRAPH_COLLAPSING);
    }

    // Output post-merge line if needed
    if (graph.state === GraphState.GRAPH_POST_MERGE) {
      const postMergeSegments: Array<{ char: string; color: number }> = [];
      const mergeChars = ['/', '|', '\\'];
      seenThis = false;
      let parentCol: Column | undefined;

      for (let i = 0; i <= graph.numColumns; i++) {
        let col: Column | undefined;
        let colNode: DagNode | null;

        if (i === graph.numColumns) {
          if (seenThis) break;
          colNode = graph.node;
          col = { node: colNode, color: graph.defaultColumnColor };
        } else {
          col = graph.columns[i];
          colNode = col?.node || null;
        }

        if (colNode === graph.node) {
          // Output merge chars for each parent
          seenThis = true;
          const parents = graph.node?.requiredBunjas || [];
          let idx = graph.mergeLayout;

          for (let j = 0; j < graph.numParents; j++) {
            const parent = parents[j];
            const parColumn = graph.findNewColumnByNode(parent);
            const c = mergeChars[idx];
            postMergeSegments.push({
              char: c,
              color: graph.newColumns[parColumn]?.color || 0,
            });

            if (idx === 2) {
              if (graph.edgesAdded > 0 || j < graph.numParents - 1) {
                postMergeSegments.push({ char: ' ', color: 0 });
              }
            } else {
              idx++;
            }
          }

          if (graph.edgesAdded === 0) {
            postMergeSegments.push({ char: ' ', color: 0 });
          }
        } else if (seenThis) {
          if (graph.edgesAdded > 0) {
            postMergeSegments.push({ char: '\\', color: col?.color || 0 });
          } else {
            postMergeSegments.push({ char: '|', color: col?.color || 0 });
          }
          postMergeSegments.push({ char: ' ', color: 0 });
        } else {
          postMergeSegments.push({ char: '|', color: col?.color || 0 });
          if (graph.mergeLayout !== 0 || i !== graph.nodeIndex - 1) {
            if (parentCol) {
              postMergeSegments.push({ char: '_', color: parentCol.color });
            } else {
              postMergeSegments.push({ char: ' ', color: 0 });
            }
          }
        }

        // Track parent column
        const firstParent = graph.node?.requiredBunjas?.[0];
        if (firstParent && colNode === firstParent) {
          parentCol = col;
        }
      }

      rows.push({
        type: 'post_merge',
        segments: postMergeSegments,
      });

      // Update state
      if (graph.isMappingCorrect()) {
        graph.updateState(GraphState.GRAPH_PADDING);
      } else {
        graph.updateState(GraphState.GRAPH_COLLAPSING);
      }
    }

    // Output collapsing lines while needed
    while (graph.state === GraphState.GRAPH_COLLAPSING) {
      const { chars, colors } = graph.outputCollapsingLine();
      const collapsingSegments: Array<{ char: string; color: number }> = [];

      for (let i = 0; i < chars.length; i++) {
        collapsingSegments.push({ char: chars[i], color: colors[i] });
      }

      rows.push({
        type: 'collapsing',
        segments: collapsingSegments,
      });
    }
  }

  return rows;
}

export function renderTextGraph(rows: GraphRow[]): string {
  const lines: string[] = [];
  rows.forEach((row) => {
    const graphChars = row.segments.map((seg) => seg.char).join('');
    if (row.type === 'node' && row.node) {
      lines.push(`${graphChars} ${row.node.debugLabel || 'Anonymous'} #${row.node.id}`);
    } else {
      lines.push(graphChars);
    }
  });
  return lines.join('\n');
}
